import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  ServerError,
} from "@apollo/client/core";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { setContext } from "@apollo/client/link/context";
import { provideApolloClient } from "@vue/apollo-composable";
import { useUserStore } from "@/stores/user";
import { onError } from "@apollo/client/link/error";
import { useCognitoAuth } from "@/cognito/cognito.config";
import { cognitoHostedLogoutUrl, cognitoSignOut } from "@/cognito/auth";
import {
  clearCompanyAppSessionAndSetDisabledAlert,
  apolloNetworkResultIsAccountDisabled,
} from "@/cognito/companyAccountDisabled";

const uploadLink = createUploadLink({
  uri: process.env.VUE_APP_API_TARGET,
});

// setContext's first arg is GraphQLRequest (no getContext in types); use prevContext.
const authLink = setContext((operation, prevContext) => {
  if (prevContext.skipAuthHeader) {
    return { headers: { ...prevContext.headers } };
  }
  const headers = prevContext.headers as Record<string, string> | undefined;
  const userStore = useUserStore();
  const token = userStore.token;
  const userType = userStore.userType;
  return {
    headers: {
      ...(headers ?? {}),
      authorization: token ? `Bearer ${token}` : "",
      // Default when Pinia hydrates before userType is set (Cognito + Frontegg GraphQL).
      userType: token ? userType || "company_user" : "",
    },
  };
});

const errorLink = onError(({ networkError, operation }) => {
  if (operation.getContext().skipUnauthorizedSignOut) {
    return;
  }
  const userStore = useUserStore();
  if (
    networkError &&
    "statusCode" in networkError &&
    networkError.statusCode === 401
  ) {
    const serverError = networkError as ServerError;
    const result = serverError?.result;
    const disabled = apolloNetworkResultIsAccountDisabled(result);
    if (disabled) {
      clearCompanyAppSessionAndSetDisabledAlert();
    } else {
      const msg =
        result && typeof result !== "string"
          ? (result as { errors?: unknown }).errors + " Please login."
          : "Please login.";
      console.warn("Apollo 401:", msg);
      if (!useCognitoAuth) {
        userStore.setToken("");
      }
    }

    if (useCognitoAuth) {
      const fallback =
        process.env.VUE_APP_COGNITO_REDIRECT_SIGN_OUT?.trim() ||
        process.env.VUE_APP_BASE_URL?.trim() ||
        "/";
      cognitoSignOut().catch(() => {
        try {
          window.location.href = cognitoHostedLogoutUrl();
        } catch {
          if (fallback) {
            window.location.href = fallback;
          }
        }
      });
    } else {
      window.location.href = `${process.env.VUE_APP_FRONTEGG_BASE_URL}/oauth/logout?post_logout_redirect_uri=${process.env.VUE_APP_BASE_URL}`;
    }
    return;
  }
});

const link = ApolloLink.from([errorLink, authLink, uploadLink]);

const apolloClient = new ApolloClient({
  link: link,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: { errorPolicy: "all" },
    query: { errorPolicy: "all" },
    mutate: { errorPolicy: "all" },
  },
});

export function setupApolloClient() {
  provideApolloClient(apolloClient);
}
